import { Button, CloseModalButton } from '@library/components/buttons';
import type { Deposit, DepositRefundResponse } from '@library/types/payments';
import { Modal } from '@library/components/modals/Modal';
import { convertCentsToDollars } from '@library/utils';
import greenCheckIcon from '@library/assets/images/icons/checked-green-icon.svg';
import processingIcon from '@library/assets/images/icons/processing-white-icon.svg';
import { useDepositMethodInfo } from '@bo-hooks/deposits/useDepositMethodInfo';
import xIcon from '@library/assets/images/icons/x-red-icon.svg';

export interface DepositsRefundStatusModalProps {
  deposits: Deposit[];
  onCancelModal: () => void;
  statuses: Record<string, Partial<DepositRefundResponse & { error: string }>>;
}

// TODO: This modal needs to be using our existing standards
// and replace all the duplicated code with our shared components

export const DepositsRefundStatusModal = ({
  deposits,
  onCancelModal,
  statuses
}: DepositsRefundStatusModalProps) => {
  const { getDepositMethodInfo } = useDepositMethodInfo();

  const depositMethodInfo = (deposit: Deposit) => {
    const { methodLine1, methodLine2 } = getDepositMethodInfo(deposit);

    return `${methodLine1} ${methodLine2}`;
  };

  const icon = (status: DepositRefundResponse['status']) => {
    switch (status) {
      case 'refunded':
        return greenCheckIcon;
      case 'failed':
        return xIcon;
      case 'refunding':
      default:
        return processingIcon;
    }
  };

  return (
    <Modal isOpen onCancelModal={onCancelModal}>
      <div
        id="image"
        className="bg-soFresh-100 outline-hidden focus:outline-hidden relative w-full flex-col rounded-lg border border-solid border-gray-600 shadow-lg"
      >
        <div className="border-soFresh-140 flex justify-between border-b border-solid p-6">
          <div className="flex h-full items-center justify-center">
            <h3 className="text-lg font-bold">Processed..</h3>
          </div>
          <CloseModalButton
            handleOnClick={onCancelModal}
            useDefaultStyling={false}
            btnClassName=""
            className="flex-1 text-right"
          />
        </div>

        <div className="text-soClean-110 border-soFresh-140 min-w-[32rem] border-b border-solid p-6 text-sm">
          <div className="mb-4">
            Your refunds have been processed.
            <ul className="mt-5">
              {deposits.map((deposit) => (
                <li
                  className="flex justify-between"
                  key={deposit.merchantReferenceId}
                >
                  <span className="flex">
                    {depositMethodInfo(deposit)} -{' '}
                    {convertCentsToDollars(deposit.amount)}
                  </span>
                  <span className="flex items-center capitalize">
                    <img
                      className="w-8 px-2"
                      src={icon(statuses[deposit.merchantReferenceId].status)}
                      alt="status icon"
                    />
                    {statuses[deposit.merchantReferenceId].status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="ml-auto flex h-full items-end justify-end px-6 py-2">
          <Button
            className="bg-soFresh-120 text-soClean-100 my-3 mr-2 flex justify-center rounded-lg p-3 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
            type="button"
            title="DepositsDetailModalCloseButton"
            onClick={onCancelModal}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};
